import { Card, CardBody } from "@progress/kendo-react-layout";
import { SovraSkeleton } from "../../../components/skeleton/sovraSkeleton";

type VolumeDriverMetric = {
    label: string;
    previousValue?: string;
    nextValue?: string;
    percentChange: string;
};

type VolumeDriversSummaryProps = {
    isLoading?: boolean;
};

const volumeDriverMetrics: VolumeDriverMetric[] = [
    {
        label: "Stores",
        previousValue: "820",
        nextValue: "860",
        percentChange: "(+5%)"
    },
    {
        label: "Velocity",
        previousValue: "12.4",
        nextValue: "14.1",
        percentChange: "(+13%)"
    },
    {
        label: "Volume Impact",
        percentChange: "+18%"
    }
];

const renderVolumeDriversSummarySkeleton = (): React.ReactNode => (
    <Card
        className="border rounded-3 bg-light-subtle shadow-sm w-100 mt-1"
        style={{ boxShadow: "0 2px 8px rgba(13, 110, 253, 0.08)" }}
    >
        <CardBody className="px-3 px-md-4 py-2">
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 gap-md-4">
                <SovraSkeleton
                    shape="text"
                    style={{ width: 118, height: 20 }}
                />

                <div className="d-flex flex-wrap align-items-center justify-content-start justify-content-md-end gap-3 gap-lg-4">
                    {Array.from({ length: 3 }, (_, index) => (
                        <div key={index} className="d-flex align-items-center gap-2 lh-sm flex-wrap">
                            <SovraSkeleton
                                shape="text"
                                style={{ width: index === 2 ? 102 : 64, height: 18 }}
                            />

                            {index < 2 && (
                                <SovraSkeleton
                                    shape="text"
                                    style={{ width: 78, height: 18 }}
                                />
                            )}

                            <SovraSkeleton
                                shape="rectangle"
                                style={{ width: 42, height: 22, borderRadius: 999 }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </CardBody>
    </Card>
);

const VolumeDriversSummary: React.FC<VolumeDriversSummaryProps> = ({ isLoading }) => {
    if (isLoading) {
        return renderVolumeDriversSummarySkeleton();
    }

    return (
        <Card className="border rounded-3 bg-light-subtle shadow-sm w-100 shadow-sm">
            <CardBody className="px-md-4 gap-3 volume-driver">
                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 gap-md-5">
                    <div
                        className="k-font-size-xs fw-bold text-uppercase flex-shrink-0 k-text-primary"
                        style={{ letterSpacing: "0.075em" }}
                    >
                        Volume drivers
                    </div>

                    <div className="d-flex flex-wrap align-items-center justify-content-start justify-content-md-end gap-3 gap-lg-4">
                        {volumeDriverMetrics.map(metric => (
                            <div key={metric.label} className="d-flex align-items-center gap-2 lh-sm flex-wrap">
                                <span className="small fw-medium k-text-subtle" style={{ color: "oklch(0.55 0.05 257.55)" }}>{`${metric.label}:`}</span>

                                {metric.previousValue && metric.nextValue && (
                                    <span className="d-inline-flex align-items-center gap-2 text-body">
                                        <span className="k-text-subtle fs-8">{metric.previousValue}</span>
                                        <span className="text-secondary fs-8">→</span>
                                        <span className="fw-semibold fs-8 k-text-primary">{metric.nextValue}</span>
                                    </span>
                                )}

                                <span className="fw-semibold k-text-success fs-8">{metric.percentChange}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default VolumeDriversSummary;
